import { EXCEL_GRID_SELECT_EDITOR_STYLES } from "../constants/excel-like-grid.constants";
import type { ExcelGridCellSelectEditorProps } from "../models/excel-grid-component-props.models";
import type { ExcelGridSelectOption } from "../models/excel-grid.models";
import { useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";

export const ExcelGridCellCustomSelectEditor = ({
  value,
  options,
  isSearchable = true,
  placeholder,
  selectMenuPortalTarget,
  onCancel,
  onCommitValue,
}: ExcelGridCellSelectEditorProps) => {
  const [inputValue, setInputValue] = useState("");
  const draftRef = useRef<string | null>(null);
  const hasFinishedEditingRef = useRef(false);

  const commitOnce = (text: string, shouldFocusGrid = true) => {
    if (hasFinishedEditingRef.current) return;
    hasFinishedEditingRef.current = true;
    onCommitValue(text.trim(), shouldFocusGrid);
  };

  const cancelOnce = () => {
    if (hasFinishedEditingRef.current) return;
    hasFinishedEditingRef.current = true;
    onCancel();
  };

  return (
    <CreatableSelect<ExcelGridSelectOption, false>
      autoFocus
      className="excel-grid-cell-select-editor text-xs leading-4"
      classNamePrefix="excel-grid-cell-select"
      aria-label="Select selected cell value"
      value={value ? { label: value, value } : null}
      inputValue={inputValue}
      options={options}
      isSearchable={isSearchable}
      isClearable
      menuIsOpen
      menuPlacement="auto"
      maxMenuHeight={220}
      menuPortalTarget={selectMenuPortalTarget ?? (typeof document === "undefined" ? undefined : document.body)}
      placeholder={placeholder}
      styles={EXCEL_GRID_SELECT_EDITOR_STYLES}
      createOptionPosition="first"
      isValidNewOption={(text) => text.trim().length > 0 && !options.some((option) => option.value === text.trim())}
      formatCreateLabel={(text) => `Use "${text.trim()}"`}
      tabSelectsValue={false}
      onInputChange={(text, { action }) => {
        if (action === "input-change") {
          draftRef.current = text;
          setInputValue(text);
        }
      }}
      onCreateOption={(text) => commitOnce(text)}
      onChange={(option) => commitOnce(option?.value ?? "")}
      onBlur={() => commitOnce(draftRef.current ?? value, false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          cancelOnce();
        } else if (event.key === "Tab") {
          event.stopPropagation();
          commitOnce(draftRef.current ?? value, false);
        }
      }}
    />
  );
};
